/**
 * Customer: Service Closure (product term) — a customer may ask FinminOS to
 * close a service before payment completes; an admin approves or declines.
 *
 * Contract: `FinminOS-SerClosure-FEGuide.md` (Aug 2026). The committed
 * `openapi.json` predates this feature, so every type here is declared locally
 * (mirrors `app/schemas/service_closure.py` in FinMin_Backend). Replace with
 * `Schemas[...]` once `pnpm gen:types` catches up.
 *
 * Endpoints:
 *   GET  /api/v1/service-closure/reasons                                                   → reason catalog (shared)
 *   POST /api/v1/customer/service-requests/{sr_id}/closure-requests                        → raise a request (201)
 *   POST /api/v1/customer/service-requests/{sr_id}/closure-requests/{closure_id}/withdraw  → drop a pending request
 *   POST /api/v1/customer/service-requests/{sr_id}/closure-requests/{closure_id}/remind    → nudge admin
 *
 * Every SR list/detail response now embeds a `service_closure` object whose
 * `can_*` flags are the ONLY source of truth for button visibility — never
 * re-derive eligibility on the client.
 */
import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ApiError, createKeyFactory } from '@finmin/api-client';
import { apiClient } from '@/lib/api';
import { queryClient } from './query-client';
import { serviceRequestKeys } from './service-requests';

// ---------- types -----------------------------------------------------------

/** Technical service state. `ON_HOLD` = a customer closure request is pending. */
export type ClosureServiceState = 'CREATED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';

export type ServiceClosureStatus =
  | 'pending_admin_action'
  | 'withdrawn_by_customer'
  | 'declined_by_admin'
  | 'approved_by_admin';

export type ServiceClosureOrigin = 'customer' | 'admin';

export interface ServiceClosureReason {
  code: string;
  label: string;
  description_required: boolean;
  children: ServiceClosureReason[];
}

export interface ServiceClosureReasonCatalog {
  customer: ServiceClosureReason[];
  admin: ServiceClosureReason[];
}

/** The `service_closure` object embedded in every customer SR response. */
export interface CustomerServiceClosure {
  request_id: string | null;
  origin: ServiceClosureOrigin | null;
  status: ServiceClosureStatus | null;
  service_state: ClosureServiceState;
  reason_code: string | null;
  reason_label: string | null;
  description: string | null;
  /** Review deadline (5 working days) — display as returned, never compute. */
  review_due_at: string | null;
  decline_reason: string | null;
  requested_at: string | null;
  decided_at: string | null;
  /** Server-provided status line, e.g. "Awaiting admin action". */
  message: string | null;
  can_request_closure: boolean;
  can_withdraw_request: boolean;
  can_remind_admin: boolean;
}

/** Body for `POST .../closure-requests`. Extra fields are rejected (422). */
export interface CreateClosureRequestPayload {
  reason_code: string;
  description?: string | null;
}

// ---------- query keys ------------------------------------------------------

export const serviceClosureKeys = createKeyFactory('service-closure', {
  reasons: () => ['reasons'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/service-closure/reasons — static catalog, shared by all roles. */
export async function fetchClosureReasons(signal?: AbortSignal) {
  const { data } = await apiClient.get<ServiceClosureReasonCatalog>(
    '/api/v1/service-closure/reasons',
    { signal },
  );
  return data;
}

/** POST /api/v1/customer/service-requests/{id}/closure-requests → 201 */
export async function createClosureRequest(
  serviceRequestId: string,
  payload: CreateClosureRequestPayload,
) {
  const { data } = await apiClient.post<CustomerServiceClosure>(
    `/api/v1/customer/service-requests/${serviceRequestId}/closure-requests`,
    payload,
  );
  return data;
}

/** POST .../closure-requests/{closure_id}/withdraw — service returns to its previous state. */
export async function withdrawClosureRequest(serviceRequestId: string, closureRequestId: string) {
  const { data } = await apiClient.post<CustomerServiceClosure>(
    `/api/v1/customer/service-requests/${serviceRequestId}/closure-requests/${closureRequestId}/withdraw`,
  );
  return data;
}

/** POST .../closure-requests/{closure_id}/remind — records a reminder + notifies admin. */
export async function remindAdminAboutClosure(serviceRequestId: string, closureRequestId: string) {
  const { data } = await apiClient.post<CustomerServiceClosure>(
    `/api/v1/customer/service-requests/${serviceRequestId}/closure-requests/${closureRequestId}/remind`,
  );
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useClosureReasons(
  options?: Omit<UseQueryOptions<ServiceClosureReasonCatalog, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ServiceClosureReasonCatalog, ApiError>({
    queryKey: serviceClosureKeys.reasons(),
    queryFn: ({ signal }) => fetchClosureReasons(signal),
    staleTime: Infinity,
    ...options,
  });
}

/** Refresh the SR after every closure command so the whole page re-renders
 *  from the authoritative `service_closure` object. */
function invalidateServiceRequest(id: string) {
  queryClient.invalidateQueries({ queryKey: serviceRequestKeys.detail(id) });
  queryClient.invalidateQueries({ queryKey: serviceRequestKeys.list() });
}

type ClosureVars = { serviceRequestId: string; closureRequestId: string };

export function useCreateClosureRequest(
  options?: UseMutationOptions<
    CustomerServiceClosure,
    ApiError,
    { serviceRequestId: string; payload: CreateClosureRequestPayload }
  >,
) {
  return useMutation<
    CustomerServiceClosure,
    ApiError,
    { serviceRequestId: string; payload: CreateClosureRequestPayload }
  >({
    mutationFn: ({ serviceRequestId, payload }) => createClosureRequest(serviceRequestId, payload),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      invalidateServiceRequest(vars.serviceRequestId);
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useWithdrawClosureRequest(
  options?: UseMutationOptions<CustomerServiceClosure, ApiError, ClosureVars>,
) {
  return useMutation<CustomerServiceClosure, ApiError, ClosureVars>({
    mutationFn: ({ serviceRequestId, closureRequestId }) =>
      withdrawClosureRequest(serviceRequestId, closureRequestId),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      invalidateServiceRequest(vars.serviceRequestId);
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

export function useRemindAdminAboutClosure(
  options?: UseMutationOptions<CustomerServiceClosure, ApiError, ClosureVars>,
) {
  return useMutation<CustomerServiceClosure, ApiError, ClosureVars>({
    mutationFn: ({ serviceRequestId, closureRequestId }) =>
      remindAdminAboutClosure(serviceRequestId, closureRequestId),
    ...options,
    onSuccess: (data, vars, onMutateResult, context) => {
      invalidateServiceRequest(vars.serviceRequestId);
      options?.onSuccess?.(data, vars, onMutateResult, context);
    },
  });
}

/**
 * Map a closure-command failure to a user-facing message. Business errors come
 * back as a plain `detail` string (400/403/404/409) and are safe to surface;
 * 422 / unexpected errors get a controlled fallback (the guide asks us to log
 * the raw detail for diagnosis rather than show it).
 */
export function closureErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 422 || err.status >= 500 || err.status === 0) {
      console.error('[service-closure]', err.status, err.payload ?? err.message);
      return fallback;
    }
    return err.message || fallback;
  }
  return fallback;
}
